import { Card, FigureLabel, FigureValue, Swatch } from '@/components/ui'
import { amountTone, cn, numberToCurrency } from '@/utils'

interface PortfolioStatStripProps {
  costBasis: number | null
  realized: number
  realizedNeedsReview: boolean
  holdingsCount: number
  accountCount: number
  reconciledCount: number
}

interface Stat {
  label: string
  value: string
  tone?: string
  hint?: string
  status?: 'ok' | 'warn'
}

// One card split into cells: the gap-px over the border colour draws the hairline rules.
export const PortfolioStatStrip = ({
  costBasis,
  realized,
  realizedNeedsReview,
  holdingsCount,
  accountCount,
  reconciledCount,
}: PortfolioStatStripProps) => {
  const allReconciled = reconciledCount === accountCount

  const stats: Stat[] = [
    {
      label: 'Cost basis',
      value: costBasis == null ? '—' : numberToCurrency(costBasis),
    },
    {
      label: 'Realized gain / loss',
      value: numberToCurrency(realized),
      tone: amountTone(realized),
      hint: realizedNeedsReview ? 'all periods, needs review' : 'all periods',
      status: realizedNeedsReview ? 'warn' : undefined,
    },
    { label: 'Holdings', value: String(holdingsCount) },
    {
      label: 'Reconciled',
      value: `${reconciledCount} of ${accountCount}`,
      status: allReconciled ? 'ok' : 'warn',
      hint: allReconciled ? 'all tie out' : 'needs review',
    },
  ]

  return (
    <div className="@container">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-2 gap-px bg-border @3xl:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white px-6 py-4">
              <FigureLabel>{stat.label}</FigureLabel>
              <div className="mt-1 flex items-center gap-1.5">
                {stat.status && (
                  <Swatch
                    className={
                      stat.status === 'ok' ? 'bg-emerald-500' : 'bg-amber-500'
                    }
                  />
                )}
                <FigureValue className={cn('text-lg', stat.tone)}>
                  {stat.value}
                </FigureValue>
              </div>
              {stat.hint && (
                <div className="mt-0.5 text-xs text-ink-subtle">
                  {stat.hint}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
